import { type FC, useState } from "react";
import { GoalDetailFutureItem } from "web_myplann/Goals/Components/GoalDetailFutureItem";
import { GoalDetailSolutionItemSkeleton } from "web_myplann/Goals/Components/GoalDetailSolutionItemSkeleton";
import {
	PinFutureContractDrawer,
	type PinFutureContractFormValues,
} from "web_myplann/Goals/Components/PinContract/PinFutureContractDrawer";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import { useFutureContractActions } from "web_myplann/Goals/Hooks/useFutureContractActions";
import type { GoalFuturePortfolioResponseDTO } from "web_myplann/Infrastructure/Api/Api";

type Props = {
	goalId: number;
	futurePortfolios: GoalFuturePortfolioResponseDTO[];
	dueYear: number;
	createOpen: boolean;
	onCloseCreate: () => void;
	onLockedAction: (() => void) | null;
};

export const GoalFutureContracts: FC<Props> = ({
	goalId,
	futurePortfolios,
	dueYear,
	createOpen,
	onCloseCreate,
	onLockedAction,
}) => {
	const [editingFuture, setEditingFuture] =
		useState<GoalFuturePortfolioResponseDTO | null>(null);

	const { submit, deleteFuture, deletingId, isPending } =
		useFutureContractActions({ goalId });

	const closeDrawer = () => {
		setEditingFuture(null);
		onCloseCreate();
	};

	const handleSubmit = (values: PinFutureContractFormValues) => {
		submit(values, editingFuture?.id ?? null, { onSuccess: closeDrawer });
	};

	const handleEdit = withLockGuard(
		onLockedAction,
		(futurePortfolio: GoalFuturePortfolioResponseDTO) =>
			setEditingFuture(futurePortfolio),
	);

	const handleDelete = withLockGuard(onLockedAction, deleteFuture);

	return (
		<>
			{futurePortfolios.map((futurePortfolio, i) =>
				futurePortfolio.id === deletingId ? (
					<GoalDetailSolutionItemSkeleton
						key={`future-${futurePortfolio.id}`}
						hasDivider={i < futurePortfolios.length - 1}
					/>
				) : (
					<GoalDetailFutureItem
						key={`future-${futurePortfolio.id}`}
						futurePortfolio={futurePortfolio}
						hasDivider={i < futurePortfolios.length - 1}
						dueYear={dueYear}
						onEdit={handleEdit}
						onDelete={handleDelete}
					/>
				),
			)}
			<PinFutureContractDrawer
				open={createOpen || editingFuture !== null}
				editing={editingFuture}
				isPending={isPending}
				onClose={closeDrawer}
				onSubmit={handleSubmit}
			/>
		</>
	);
};
